// Playback clock, looping, frame prefetch and degrade policy (F-02, D-23, D-27).

import type { SourceId } from '../core/types';
import { clampTick, stepTick, tickDurationSec } from '../core/timeline';
import type { Store } from './store';
import type { Sources } from './sources';
import { AudioPlayer } from '../media/audio';

export interface DisplayChoice {
  /** Frame index to show for each source (null = out of range / black). */
  frames: Map<SourceId, number | null>;
  /** Sources shown from the proxy (L1) instead of full resolution. */
  proxy: Set<SourceId>;
  /** Sources whose frame is not available yet (neither full nor proxy). */
  pending: Set<SourceId>;
}

export class Player {
  readonly audio = new AudioPlayer();
  private startWall = 0;
  private startTick = 0;
  private pingPongDir = 1;
  private raf = 0;
  /** Frames skipped because decoding could not keep up (quality mode), since play started. */
  dropped = 0;
  private lastShownTick = -1;
  private audioSourceKey = '';
  private usingAudioClock = false;
  audioAvailable = false;
  onFrame: (() => void) | null = null;

  constructor(
    private store: Store,
    private sources: Sources,
  ) {}

  get tick(): number {
    return this.store.state.project.playback.tick;
  }

  private setTick(t: number): void {
    // The playhead is not undoable and does not mark the project dirty on its own.
    this.store.state.project.playback.tick = t;
  }

  get playing(): boolean {
    return this.store.state.ui.playing;
  }

  /** Playback rate in ticks per second (signed). */
  private rate(): number {
    const tl = this.sources.timeline;
    const s = this.store.state.project.playback.speed * this.store.state.ui.shuttle * this.pingPongDir;
    return s / tickDurationSec(tl);
  }

  private audible(): boolean {
    const p = this.store.state.project;
    const r = p.playback.speed * this.store.state.ui.shuttle * this.pingPongDir;
    return Math.abs(r - 1) < 1e-9 && p.audioSourceId !== null;
  }

  async syncAudioSource(): Promise<void> {
    const id = this.store.state.project.audioSourceId;
    const rt = id ? this.sources.runtime.get(id) : undefined;
    const key = id && rt ? `${id}:${rt.client.uid}` : '';
    if (key === this.audioSourceKey) return;
    this.audioSourceKey = key;
    this.audioAvailable = await this.audio.setFile(rt && rt.info.hasAudio ? (rt.client.media as File) : null);
    this.audio.setVolume(this.store.state.project.audioVolume);
    if (this.playing) this.restartClock();
  }

  play(): void {
    if (this.playing) return;
    const tl = this.sources.timeline;
    const p = this.store.state.project.playback;
    const { lo, hi } = this.bounds();
    // Starting at the out point (or the end) restarts from the in point.
    const dir = Math.sign(this.store.state.ui.shuttle) || 1;
    if (dir > 0 && this.tick >= hi) this.setTick(lo);
    if (dir < 0 && this.tick <= lo) this.setTick(hi);
    this.pingPongDir = 1;
    this.dropped = 0;
    this.store.ui((u) => (u.playing = true));
    for (const id of this.sources.loadedIds()) this.sources.client(id)?.pauseScan(true);
    void tl;
    void p;
    this.restartClock();
    this.loop();
  }

  pause(): void {
    if (!this.playing) return;
    this.store.ui((u) => {
      u.playing = false;
      u.shuttle = 1;
    });
    cancelAnimationFrame(this.raf);
    this.audio.stop();
    this.usingAudioClock = false;
    this.setTick(Math.round(this.lastShownTick >= 0 ? this.lastShownTick : this.tick));
    for (const id of this.sources.loadedIds()) this.sources.client(id)?.pauseScan(false);
    // Paused frames must be the exact decoded planes (F-02.8): request them now and release GPU frames.
    this.prefetch(this.tick);
    this.store.notify();
    this.onFrame?.();
  }

  toggle(): void {
    if (this.playing) this.pause();
    else this.play();
  }

  /** J/K/L shuttle (F-02.4): J = reverse faster, K = pause, L = forward faster. */
  shuttle(dir: -1 | 0 | 1): void {
    if (dir === 0) {
      this.pause();
      return;
    }
    const cur = this.store.state.ui.shuttle;
    let next: number;
    if (!this.playing) next = dir;
    else if (Math.sign(cur) !== dir) next = dir;
    else next = Math.min(8, Math.abs(cur) * 2) * dir;
    this.store.ui((u) => (u.shuttle = next));
    if (!this.playing) this.play();
    else this.restartClock();
  }

  private restartClock(): void {
    this.startWall = performance.now();
    this.startTick = this.tick;
    this.lastShownTick = this.tick;
    this.audio.stop();
    this.usingAudioClock = false;
    if (this.audible() && this.audioAvailable) {
      const t = this.audioTimeForTick(this.tick);
      if (t !== null) {
        this.usingAudioClock = true;
        void this.audio.start(t);
      }
    }
  }

  /** Audio media time for a tick, following the audio source's frame mapping and offset (F-02.11). */
  private audioTimeForTick(tick: number): number | null {
    const id = this.store.state.project.audioSourceId;
    if (!id) return null;
    const rt = this.sources.runtime.get(id);
    const src = this.sources.source(id);
    if (!rt || !src) return null;
    const tl = this.sources.timeline;
    const secOnA = tick * tickDurationSec(tl);
    const offsetSec = id === 'A' ? 0 : src.frameOffset / src.spec.frameRate;
    return rt.info.firstTimestamp + secOnA + offsetSec;
  }

  private tickForAudioTime(t: number): number {
    const id = this.store.state.project.audioSourceId!;
    const rt = this.sources.runtime.get(id)!;
    const src = this.sources.source(id)!;
    const offsetSec = id === 'A' ? 0 : src.frameOffset / src.spec.frameRate;
    return (t - rt.info.firstTimestamp - offsetSec) / tickDurationSec(this.sources.timeline);
  }

  bounds(): { lo: number; hi: number } {
    const tl = this.sources.timeline;
    const p = this.store.state.project.playback;
    const lo = p.inPoint !== undefined ? clampTick(tl, p.inPoint) : 0;
    const hi = p.outPoint !== undefined ? clampTick(tl, p.outPoint) : tl.totalTicks - 1;
    return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
  }

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    if (!this.playing) return;
    let target: number;
    if (this.usingAudioClock && this.audio.running) {
      target = this.audio.clockStarted ? this.tickForAudioTime(this.audio.mediaTime()) : this.startTick;
    } else {
      target = this.startTick + ((performance.now() - this.startWall) / 1000) * this.rate();
    }
    const { lo, hi } = this.bounds();
    const mode = this.store.state.project.playback.loopMode;
    if (target > hi + 0.999 || target < lo) {
      const forward = target > hi;
      if (mode === 'once') {
        // Stop on the end tick only once its frames are ready (quality mode holds the tick on screen until then), so
        // the panes never go blank at the out point while the exact planes are decoded (rule 8).
        const end = forward ? hi : lo;
        const shown = this.chooseShownTick(end);
        this.lastShownTick = shown;
        this.setTick(shown);
        if (shown === end) this.pause();
        else {
          this.prefetch(end, forward ? 1 : -1, shown);
          this.onFrame?.();
        }
        return;
      }
      if (mode === 'ping-pong') {
        this.pingPongDir = -this.pingPongDir;
        this.setTick(forward ? hi : lo);
      } else {
        this.setTick(forward ? lo : hi);
      }
      this.restartClock();
      target = this.tick;
    }
    const want = Math.max(lo, Math.min(hi, Math.floor(target + 1e-6)));
    const shown = this.chooseShownTick(want);
    if (shown !== this.lastShownTick) {
      if (shown !== want) this.dropped += 1;
      this.lastShownTick = shown;
    }
    this.setTick(shown);
    this.prefetch(want, Math.sign(this.rate()) || 1, shown);
    this.onFrame?.();
  };

  /**
   * Quality mode keeps full resolution and holds the last tick whose frames are all ready (drops frames);
   * smooth mode shows the wanted tick and lets the view fall back to proxies (D-27).
   */
  private chooseShownTick(want: number): number {
    const smooth = this.store.state.settings.degradeMode === 'smooth';
    if (this.allReady(want, smooth)) return want;
    if (this.lastShownTick < 0) return want;
    // The newest tick between the one on screen and the clock whose frames are all ready (frames skipped in between
    // count as dropped). Otherwise hold the tick on screen: its textures stay uploaded even if the frame data has been
    // released, so all sources remain on the same tick (F-02.7) instead of showing black.
    const dir = Math.sign(want - this.lastShownTick);
    if (dir !== 0) {
      for (let t = want - dir; t !== this.lastShownTick; t -= dir) if (this.allReady(t, smooth)) return t;
    }
    return this.lastShownTick;
  }

  private allReady(tick: number, allowProxy: boolean): boolean {
    for (const id of this.sources.loadedIds()) {
      const f = this.sources.frameAt(id, tick)?.frame;
      if (f === null || f === undefined) continue;
      const c = this.sources.client(id)!;
      if (c.hasAny(f)) continue;
      // A proxy counts only once decoded: the view cannot draw a compressed one yet (it would show black).
      if (allowProxy && c.hasProxyBitmap(f)) continue;
      if (allowProxy) c.warmProxies([f]);
      return false;
    }
    return true;
  }

  /** Requests the frames around the playhead for every source. */
  prefetch(tick = this.tick, dir = 1, keepTick = tick): void {
    const tl = this.sources.timeline;
    const playing = this.playing;
    // Look-ahead in A frames; capped so the GPU frame cache can hold the whole requested window (a smaller cache
    // would evict frames that were just decoded and force the GOP to be decoded again).
    const ahead = playing ? Math.min(24, Math.max(12, Math.round(Math.abs(this.rate()) * tickDurationSec(tl) * 12))) : 2;
    for (const id of this.sources.loadedIds()) {
      const c = this.sources.client(id)!;
      const list: number[] = [];
      const seen = new Set<number>();
      const push = (f: number | null | undefined) => {
        if (f === null || f === undefined || seen.has(f)) return;
        seen.add(f);
        list.push(f);
      };
      // The frame on screen first (pinned), then the wanted one and the look-ahead.
      if (playing) push(this.sources.frameAt(id, keepTick)?.frame);
      const cur = this.sources.frameAt(id, tick)?.frame;
      push(cur);
      if (dir < 0 && cur !== null && cur !== undefined) {
        // Reverse: decoding must start at the key frame; request the GOP window behind the playhead.
        const keys = this.sources.runtime.get(id)!.info.keyFrames;
        let k = 0;
        for (const kf of keys) if (kf <= cur) k = kf;
        for (let f = cur - 1; f >= Math.max(k, cur - 24); f--) push(f);
      }
      const maxFrames = playing ? 28 : 6;
      for (let i = 1; i <= ahead * tl.ticksPerFrame && list.length < maxFrames; i++) push(this.sources.frameAt(id, tick + i * dir)?.frame);
      if (!playing) push(this.sources.frameAt(id, tick - dir)?.frame);
      // Playback uses the GPU path; paused frames are fetched as exact planes (S-03).
      if (playing) c.gpuLimit = Math.max(24, list.length + 4);
      c.request(list, list, playing ? 'frame' : 'planes');
      // Proxies around the playhead are decoded ahead: they stand in while the exact frames decode (scrubbing, D-27).
      if (!playing && cur !== null && cur !== undefined) {
        const near: number[] = [];
        for (let d = 0; d <= 12; d++) near.push(cur + d * dir, cur - d * dir);
        c.warmProxies(near.filter((f) => f >= 0));
      } else if (playing) c.warmProxies(list);
      if (playing && cur !== null && cur !== undefined) {
        // Never release the frame currently on screen (it may lag the wanted tick while holding, D-27).
        const shownF = this.sources.frameAt(id, keepTick)?.frame ?? cur;
        const lo = dir > 0 ? Math.min(cur, shownF) - 1 : cur - 30;
        const hi = dir > 0 ? cur + ahead * tl.ticksPerFrame + 2 : Math.max(cur, shownF) + 1;
        c.retainGpu(lo, hi);
      } else if (!playing) {
        // Keep the GPU frame of the tick on screen until its exact planes replace it: the view renders on its own
        // animation frame, possibly after this call, and would otherwise find nothing to show (rule 8).
        const f = cur ?? -1;
        if (c.hasFrame(f)) c.retainGpu(-1, -1);
        else c.retainGpu(f, f);
      }
    }
  }

  seek(tick: number): void {
    const t = clampTick(this.sources.timeline, tick);
    this.setTick(t);
    this.lastShownTick = t;
    if (this.playing) this.restartClock();
    this.prefetch(t);
    this.store.notify();
    this.onFrame?.();
  }

  step(delta: number): void {
    if (this.playing) this.pause();
    const t = stepTick(this.sources.timeline, this.tick, delta, this.store.state.project.originalFramesOnly);
    this.seek(t);
  }

  /** Current display choice per source for the view. */
  display(tick = this.tick): DisplayChoice {
    const frames = new Map<SourceId, number | null>();
    const proxy = new Set<SourceId>();
    const pending = new Set<SourceId>();
    const allowProxy = this.playing && this.store.state.settings.degradeMode === 'smooth';
    for (const id of this.sources.loadedIds()) {
      const f = this.sources.frameAt(id, tick)?.frame ?? null;
      frames.set(id, f);
      if (f === null) continue;
      const c = this.sources.client(id)!;
      if (c.hasFrame(f) || (this.playing && c.hasGpuFrame(f))) continue;
      if (c.hasGpuFrame(f)) continue;
      if (c.hasProxy(f) && (allowProxy || !this.playing)) proxy.add(id);
      else pending.add(id);
    }
    return { frames, proxy, pending };
  }
}
